// Libs
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User } from "firebase/auth";

// Local
import EmailTemplateForm from "../../components/EmailMarketing/EmailTemplateForm";
import { useAuthStore } from "../../store/firebase-auth";
import { EMAIL_TEMPLATES_URL, EMAIL_TEMPLATES_EDIT_URL } from "../../urls";
import { DbRead } from "../../database";
import {
  EmailTemplateStatus,
  ExistingEmailTemplate,
} from "../../models/email-template";
import { useToastMessageStore } from "../../store/toast-messages";

interface Props {
  siteKey: string;
}

interface FormData {
  name: string;
  subject: string;
  description?: string;
  htmlContent: string;
  status: EmailTemplateStatus;
}

export default function CreateEmailTemplateContainer({ siteKey }: Props) {
  const navigate = useNavigate();
  const firebaseUser = useAuthStore((state) => state.firebaseUser) as User;
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );
  const [saving, setSaving] = useState(false);
  const [conversationID, setConversationID] = useState<string | undefined>();
  const [templates, setTemplates] = useState<ExistingEmailTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [pendingTemplateName, setPendingTemplateName] = useState<string | null>(
    null,
  );
  const [wasCopiedFromTemplate, setWasCopiedFromTemplate] = useState(false);

  // Load existing templates for copying
  useEffect(() => {
    if (!siteKey) {
      setTemplatesLoading(false);
      return;
    }

    // Subscribe to real-time updates of email templates
    const unsubscribe = DbRead.emailTemplates.subscribe({
      siteKey,
      onChange: (fetchedTemplates) => {
        setTemplates(fetchedTemplates);
        setTemplatesLoading(false);

        // Check if we're waiting for a newly created template
        if (pendingTemplateName && wasCopiedFromTemplate) {
          const newTemplate = fetchedTemplates.find(
            (t) => t.name === pendingTemplateName,
          );
          if (newTemplate) {
            // Found the newly created template, navigate to its edit page
            setPendingTemplateName(null);
            setWasCopiedFromTemplate(false);
            navigate(`${EMAIL_TEMPLATES_EDIT_URL}/${newTemplate.id}`);
          }
        }
      },
      onError: (error) => {
        console.error("Error fetching templates for copying:", error);
        setTemplatesLoading(false);
      },
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [siteKey]);

  const handleSubmit = async (data: FormData) => {
    if (!firebaseUser) {
      addToastMessage({
        id: `auth-error-${Date.now()}`,
        message: "You must be logged in to create templates",
        type: "error",
        dialog: false,
      });
      return;
    }

    setSaving(true);

    try {
      // If this was copied from a template, set up navigation to edit page
      if (wasCopiedFromTemplate) {
        setPendingTemplateName(data.name);
      }

      // Create the email template using our database function
      await DbRead.emailTemplates.create({
        siteKey,
        name: data.name,
        description: data.description,
        htmlContent: data.htmlContent,
        subject: data.subject,
        status: data.status,
      });

      // Show success message
      addToastMessage({
        id: `template-created-${Date.now()}`,
        message: `Email template "${data.name}" created successfully!`,
        type: "success",
        dialog: false,
      });

      // Navigate based on whether this was copied from another template
      if (!wasCopiedFromTemplate) {
        // If created from scratch, navigate back to templates list immediately
        navigate(EMAIL_TEMPLATES_URL);
      }
      // If copied, the navigation will happen in the subscription onChange
      // when the new template appears
    } catch (error) {
      console.error("Error creating template:", error);

      // Show error message but keep user on the form
      addToastMessage({
        id: `template-error-${Date.now()}`,
        message:
          error instanceof Error
            ? `Failed to create template: ${error.message}`
            : "Failed to create template. Please try again.",
        type: "error",
        dialog: false,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateWithAI = async (data: {
    description: string;
    conversationID?: string;
  }) => {
    try {
      const result = await DbRead.stilton.generateEmailTemplate({
        siteKeyID: siteKey,
        description: data.description,
        conversationID: data.conversationID,
      });

      setConversationID(result.conversationID);
      return result;
    } catch (error) {
      console.error("Error generating template with AI:", error);
      throw error;
    }
  };

  const handleEditWithAI = async (data: {
    editInstructions: string;
    currentHtmlContent: string;
    currentSubject: string;
    conversationID?: string;
  }) => {
    try {
      const result = await DbRead.stilton.editEmailTemplate({
        siteKeyID: siteKey,
        editInstructions: data.editInstructions,
        currentHtmlContent: data.currentHtmlContent,
        currentSubject: data.currentSubject,
        conversationID: data.conversationID,
      });

      setConversationID(result.conversationID);
      return result;
    } catch (error) {
      console.error("Error editing template with AI:", error);
      throw error;
    }
  };

  const handleCancel = () => {
    navigate(EMAIL_TEMPLATES_URL);
  };

  const handleTemplateCopied = () => {
    setWasCopiedFromTemplate(true);
  };

  return (
    <EmailTemplateForm
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isEditing={false}
      isLoading={saving}
      templates={templates}
      templatesLoading={templatesLoading}
      siteKey={siteKey}
      onGenerateWithAI={handleGenerateWithAI}
      onEditWithAI={handleEditWithAI}
      conversationID={conversationID}
      onTemplateCopied={handleTemplateCopied}
    />
  );
}
